// Reordering on a phone: an "Edit order" toggle in the app bar, and ↑/↓ on each
// row while it is on.
//
// ── Why arrows and not drag ────────────────────────────────────────────────
// The desktop shell offers both, and drag is the better of the two THERE. Here
// it is the worse one: every list this applies to lives inside a vertical
// scroller, so a long-press-and-drag competes with the scroll gesture, and the
// item being dragged has to travel past the edge of a 6-inch screen to reach
// anywhere useful. Arrows cost one tap per position and never fight the scroll.
//
// ⚠️ THE ORDER IS THE UNFILTERED ONE. Both the arrows and the writes address
// rows by their INDEX, so a list with "hide offline" applied would persist an
// order computed from a subset — silently moving devices the operator cannot
// see. Every caller suspends its filter while `editOrder` is on; DesktopShell
// carries the same note for the same reason.
//
// ⚠️ AND IT IS A PER-SCREEN GATE, not a global permission. `canReorder` is
// Super Admin OR the owner of the SELECTED location, so it is only meaningful
// once a location is in context. The Locations tab passes it too, because
// reordering your own list of sites is the one case that needs no location.

import React from 'react';
import { Box, IconButton, Button, Tooltip } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';

/**
 * The app-bar toggle. Render into an app bar's `actions` slot.
 *
 * Deliberately a labelled Button rather than an icon: "what does this arrow
 * do" is a question an icon alone cannot answer, and the mode it enters changes
 * what every tap on the screen below means.
 */
export function EditOrderButton({ editing, onToggle, color }) {
  return (
    <Button
      size="small"
      variant={editing ? 'contained' : 'text'}
      startIcon={<SwapVertIcon />}
      onClick={onToggle}
      sx={{
        whiteSpace: 'nowrap',
        color: editing ? undefined : color,
        ...(editing ? {} : { border: `1px solid ${color}55` }),
      }}
    >
      {editing ? 'Done' : 'Order'}
    </Button>
  );
}

/**
 * The ↑/↓ pair for one row. `index` and `count` are positions in the FULL list.
 *
 * `stopPropagation` on both: these sit inside cards whose whole surface opens
 * the thing, and without it every reorder tap would also navigate away from the
 * list being reordered.
 */
export default function ReorderArrows({ index, count, onMove, color, vertical = false }) {
  const move = (dir) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    onMove(index, dir);
  };
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      sx={{ display: 'flex', flexDirection: vertical ? 'column' : 'row', flexShrink: 0 }}
    >
      <Tooltip title="Move up">
        <span>
          <IconButton size="small" disabled={index === 0} onClick={move(-1)} sx={{ color }}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Move down">
        <span>
          <IconButton
            size="small"
            disabled={index >= count - 1}
            onClick={move(1)}
            sx={{ color }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
